<template>
  <div class="fanbase-page">
    <MainNavbar />

    <main class="fanbase-main">
      <header class="fanbase-header">
        <h1>
          <i class="fa-solid fa-users" aria-hidden="true" />
          Tournament fanbase
        </h1>
        <p>
          Discover football tournaments on IPA — dates, venues, prize pools, and how teams or hosts
          contribute.
        </p>
      </header>

      <div class="fanbase-toolbar">
        <span class="fanbase-count">
          <template v-if="!loading">{{ tournaments.length }} tournament(s)</template>
        </span>
        <router-link class="fanbase-link" :to="{ name: 'TournamentLanding' }">
          <i class="fa-solid fa-arrow-left" aria-hidden="true" />
          Tournament hub
        </router-link>
      </div>

      <p v-if="loading" class="fanbase-loading" role="status">
        <i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
        Loading tournaments…
      </p>

      <p v-else-if="loadError" class="fanbase-error" role="alert">{{ loadError }}</p>

      <div v-else-if="!tournaments.length" class="fanbase-empty" role="status">
        <i class="fa-solid fa-trophy" aria-hidden="true" style="font-size: 2rem; margin-bottom: 0.75rem" />
        <p>No tournaments in the fanbase yet. Check back soon or create one as a host.</p>
        <router-link class="fanbase-link" :to="{ name: 'TournamentRegistration' }" style="margin-top: 1rem">
          Create a tournament
        </router-link>
      </div>

      <div v-else class="fanbase-grid">
        <article
          v-for="item in tournaments"
          :key="item.tournament_number"
          class="fanbase-card"
        >
          <div class="fanbase-card__poster">
            <img
              v-if="item.poster_url"
              :src="item.poster_url"
              :alt="`Poster for ${cardTitle(item)}`"
              loading="lazy"
            />
            <div v-else class="fanbase-card__poster-placeholder" aria-hidden="true">
              <i class="fa-solid fa-image" />
            </div>
            <span
              class="fanbase-card__badge"
              :class="`fanbase-card__badge--${tournamentStatusClass(item)}`"
            >
              {{ tournamentStatusLabel(item) }}
            </span>
            <span
              v-if="item.has_live_fixtures"
              class="fanbase-card__live-badge"
              aria-label="Matches in progress"
            >
              <i class="fa-solid fa-circle" aria-hidden="true" />
              Live
            </span>
          </div>

          <div class="fanbase-card__body">
            <h2 class="fanbase-card__title">{{ cardTitle(item) }}</h2>
            <p v-if="item.winner_team_name" class="fanbase-card__winner">
              <i class="fa-solid fa-trophy" aria-hidden="true" />
              Champion: {{ item.winner_team_name }}
            </p>

            <div class="fanbase-card__meta">
              <span>
                <i class="fa-solid fa-calendar-days" aria-hidden="true" />
                {{ item.date_summary }}
              </span>
              <span>
                <i class="fa-solid fa-location-dot" aria-hidden="true" />
                {{ item.venue_summary }}
              </span>
              <span>
                <i class="fa-solid fa-users" aria-hidden="true" />
                {{ item.teams_registered }}/{{ item.participant_teams }} teams
              </span>
            </div>

            <div class="fanbase-card__prize">
              <div class="fanbase-card__prize-label">Cash prize pool</div>
              <div class="fanbase-card__prize-value">{{ currency(item.cash_prize) }}</div>
              <p class="fanbase-card__contribution">{{ contributionLine(item) }}</p>
            </div>

            <router-link
              class="fanbase-card__btn"
              :to="{
                name: 'TournamentFanbaseDetail',
                params: { tournamentNumber: String(item.tournament_number) },
              }"
            >
              View more details
              <i class="fa-solid fa-arrow-right" aria-hidden="true" />
            </router-link>
          </div>
        </article>
      </div>
    </main>

    <MainFooter />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from "vue";
import MainNavbar from "@/components/main/headernfooter/header.vue";
import MainFooter from "@/components/main/headernfooter/Footer.vue";
import { buildApiUrl } from "@/config/api";
import { startLiveRefresh } from "@/utils/liveRefresh";
import {
  TOURNAMENT_FANBASE_API,
  tournamentStatusClass,
  tournamentStatusLabel,
} from "@/routes/tournament/constants";

const PLAYER_FORMAT_LABELS = {
  6: "6 aside",
  7: "7 aside",
  11: "11 aside",
};

const loading = ref(true);
const loadError = ref("");
const tournaments = ref([]);

function currency(value) {
  return `KSH ${Number(value || 0).toLocaleString()}`;
}

function cardTitle(item) {
  const format = PLAYER_FORMAT_LABELS[item.player_format] || `${item.player_format} aside`;
  return `${item.age_bracket} · ${format} · ${item.team_category}`;
}

function contributionLine(item) {
  const c = item.contribution || {};
  if (c.model === "teams") {
    return `${c.label}: ${currency(c.amount)} per team (pool ${currency(c.total_pool)})`;
  }
  return `${c.label}: ${currency(c.amount)}`;
}

async function loadFanbase(silent = false) {
  if (!silent) {
    loading.value = true;
    loadError.value = "";
  }
  try {
    const response = await fetch(buildApiUrl(TOURNAMENT_FANBASE_API.list));
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.detail || data.message || "Failed to load tournaments.");
    }
    tournaments.value = data.tournaments || [];
  } catch (err) {
    if (!silent) {
      loadError.value = err.message || "Could not load tournament fanbase.";
      tournaments.value = [];
    }
  } finally {
    if (!silent) {
      loading.value = false;
    }
  }
}

let stopLiveRefresh = null;

onMounted(() => {
  loadFanbase();
  stopLiveRefresh = startLiveRefresh(() => loadFanbase(true));
});

onUnmounted(() => {
  stopLiveRefresh?.();
});
</script>

<style scoped src="./fanbase.css"></style>
